import { Effect, Option } from 'effect';

import { CustomerAccount } from '@vs-shop/engine/capability';
import { capabilityLayer } from '@vs-shop/engine/capability/capabilityLayer';
import type {
  ChangeCustomerPassword,
  SaveCustomerAddress,
  UpdateCustomerProfile,
} from '@vs-shop/engine/capability/customerAccount/CustomerAccount';
import type {
  CustomerAddress,
  CustomerProfile,
} from '@vs-shop/engine/capability/customerAccount/customerAccountSchema';
import { CustomerAccountError } from '@vs-shop/engine/capability/customerAccount/errors';

import type { CustomerMe } from '@vs-shop/schemas/branded/CustomerSchema';

import { requestCustomerMe, updateCustomer } from '../../bind/customer';
import { createAddress, updateAddress } from '../../bind/misc';
import { updatePassword } from '@vs-shop/engine/request/backendApi/customer/updatePassword';

const toGender = (value: string): 'u' | 'm' | 'f' => {
  if (value === 'u' || value === 'm' || value === 'f') return value;
  return 'u';
};

const toAddress = (address: NonNullable<CustomerMe['defaultAddress']>): CustomerAddress => ({
  id: String(address.id ?? 0),
  firstName: address.firstName,
  lastName: address.lastName,
  street: address.street,
  city: address.city,
  postcode: address.postcode,
  countryCode: address.countryCode,
  company: Option.fromNullishOr(address.company),
  businessId: Option.fromNullishOr(address.businessId),
  vatNumber: Option.fromNullishOr(address.vatNumber),
  phoneNumber: Option.fromNullishOr(address.phoneNumber),
});

const toProfile = (customer: CustomerMe): CustomerProfile => ({
  id: String(customer.id ?? 0),
  email: customer.email,
  firstName: Option.fromNullishOr(customer.firstName),
  lastName: Option.fromNullishOr(customer.lastName),
  fullName: Option.fromNullishOr(customer.fullName),
  phoneNumber: Option.fromNullishOr(customer.phoneNumber),
  subscribedToNewsletter: customer.subscribedToNewsletter,
  defaultAddress: Option.map(Option.fromNullishOr(customer.defaultAddress), toAddress),
});

const readCustomer = requestCustomerMe;

const profile = readCustomer.pipe(
  Effect.map(toProfile),
  Effect.mapError(CustomerAccountError.fromRequestFailure('profile'))
);

const customerFor = (operation: string) =>
  readCustomer.pipe(Effect.mapError(CustomerAccountError.fromWriteFailure(operation)));

const profileAfter = (operation: string) => customerFor(operation).pipe(Effect.map(toProfile));

const addressIri = (id: number | string): string => `/api/v2/shop/addresses/${String(id)}`;

const toCustomerBody = (
  current: CustomerMe,
  changes: Partial<{
    readonly firstName: string;
    readonly lastName: string;
    readonly email: string;
    readonly phoneNumber: string;
    readonly defaultAddress: string;
  }>
) => ({
  email: changes.email ?? current.email,
  firstName: changes.firstName === undefined ? current.firstName : changes.firstName || null,
  lastName: changes.lastName === undefined ? current.lastName : changes.lastName || null,
  phoneNumber:
    changes.phoneNumber === undefined ? current.phoneNumber : changes.phoneNumber || null,
  birthday: current.birthday?.value ?? null,
  gender: toGender(current.gender),
  subscribedToNewsletter: current.subscribedToNewsletter,
  defaultAddress:
    changes.defaultAddress ??
    Option.getOrNull(
      Option.map(Option.fromNullishOr(current.defaultAddress), (address) =>
        addressIri(address.id ?? 0)
      )
    ),
});

const updateProfile = (input: UpdateCustomerProfile) =>
  Effect.gen(function* () {
    const current = yield* customerFor('updateProfile');
    const id = String(current.id ?? 0);
    yield* updateCustomer(id, toCustomerBody(current, input)).pipe(
      Effect.mapError(CustomerAccountError.fromWriteFailure('updateProfile', id))
    );
    return yield* profileAfter('updateProfile');
  });

const changePassword = (input: ChangeCustomerPassword) =>
  Effect.gen(function* () {
    const current = yield* customerFor('changePassword');
    const id = String(current.id ?? 0);
    yield* updatePassword(id, input).pipe(
      Effect.mapError(CustomerAccountError.fromWriteFailure('changePassword', id))
    );
  });

const toAddressBody = (input: SaveCustomerAddress) => ({
  firstName: input.firstName,
  lastName: input.lastName,
  street: input.street,
  city: input.city,
  postcode: input.postcode,
  countryCode: input.countryCode,
  company: input.company.length === 0 ? null : input.company,
  businessId: input.businessId.length === 0 ? null : input.businessId,
  vatNumber: input.vatNumber.length === 0 ? null : input.vatNumber,
  phoneNumber: input.phoneNumber.length === 0 ? null : input.phoneNumber,
  provinceCode: null,
  provinceName: null,
});

const saveDefaultAddress = (input: SaveCustomerAddress) =>
  Effect.gen(function* () {
    const current = yield* customerFor('saveDefaultAddress');
    const id = String(current.id ?? 0);
    const body = toAddressBody(input);

    if (current.defaultAddress === null) {
      const created = yield* createAddress(body).pipe(
        Effect.mapError(CustomerAccountError.fromWriteFailure('saveDefaultAddress'))
      );
      yield* updateCustomer(
        id,
        toCustomerBody(current, { defaultAddress: addressIri(created.id ?? '') })
      ).pipe(Effect.mapError(CustomerAccountError.fromWriteFailure('saveDefaultAddress', id)));
    } else {
      const addressId = String(current.defaultAddress.id ?? 0);
      yield* updateAddress(addressId, body).pipe(
        Effect.mapError(CustomerAccountError.fromWriteFailure('saveDefaultAddress', addressId))
      );
    }

    return yield* profileAfter('saveDefaultAddress');
  });

/** RCProfi implements the same account interface as RacingLine. */
export const CustomerAccountLive = capabilityLayer(CustomerAccount, {
  profile,
  updateProfile,
  changePassword,
  saveDefaultAddress,
});
